import { useEffect, useRef, useState, useMemo } from "react";
import { Watch } from "react-loader-spinner";
import Router, { useRouter } from "next/router";
import { AiOutlineCamera } from "react-icons/ai";
import UploadButton from "../components/UploadButton";
import { useProgress } from "@react-three/drei";
import { exportGLTF } from "../lib/GLTF";
import ToolHeader from "/components/ToolHeader";

import {
  Button,
  ColorPicker,
  SliderOption,
  SwitchOption,
} from "../components/ui";

import Image from "next/image";

import { styled } from "/stitches.config.js";
import { Leva } from "leva";
import { useSession } from "next-auth/react";

import { useQuota } from "/lib/useQuota";

import Profile from "/components/Profile";
import { SignInDialog } from "/components/SignIn";
import MobileOnly from "/components/MobileOnly";
import Gallery from "/components/Gallery";

import dynamic from "next/dynamic";
const LGL = dynamic(() => import("../components/LGL"), { ssr: false });

export default function Packshot({ isMobile }) {
  const isAuth = useAuth();
  const router = useRouter();
  const { pathname, query } = useRouter();
  const {
    url = "/models/coffee.glb",
    leva = false,
    backupProject = false,
  } = query;

  const [env] = useState("white");
  const [envMapIntensity, setEnvMapIntensity] = useState(10);
  const [roughness, setRoughness] = useState(40);
  const [metalness, setMetalness] = useState(0);
  const [color, setColor] = useState("#fff");
  const [lights, setLights] = useState(["gt", "l", "r"]);
  const [transparentBackdrop, setTransparentBackdrop] = useState(false);

  const [renderState, setRenderState] = useState(null);
  const options = {
    backdrop: {
      roughness: roughness / 100,
      metalness: metalness / 100,
      transparent: transparentBackdrop,
      color,
    },
    onStateChanged: setRenderState,
    envMapIntensity: envMapIntensity / 100,
    lights,
  };

  const loading = useProgress((state) => !state.loaded);
  const sceneKey = [
    url,
    env,
    roughness,
    metalness,
    color,
    envMapIntensity,
    transparentBackdrop,
    lights,
  ];
  const cachedLGL = useMemo(
    () => <StyledLGL env={env} options={options} url={url} />,
    [...sceneKey]
  );

  function redirectUrl(url) {
    Router.push(
      {
        pathname,
        query: {
          ...query,
          url,
        },
      },
      undefined,
      { shallow: true }
    );
  }

  const { refresh: refreshQuota, used, total } = useQuota();
  const [enable4K, setEnable4K] = useState(false);
  useEffect(() => {
    if (total - used <= 0) {
      setEnable4K(false);
    } else if (isAuth && router.query.enable4K) {
      setEnable4K(true);
    }
  }, [isAuth, used, total]);

  let switch4K = (
    <SwitchOption
      checked={enable4K}
      label="High Resolution (2048x2048)"
      onCheckedChange={setEnable4K}
      disabled={!isAuth || total - used <= 0}
    />
  );

  if (!isAuth) {
    const callbackUrl = new URL(router.asPath, "https://foobar");
    callbackUrl.searchParams.set("enable4K", 1);
    switch4K = (
      <SignInDialog
        callbackUrl={callbackUrl.toString().replace("https://foobar", "")}
      >
        <div style={{ cursor: "pointer" }}>{switch4K}</div>
      </SignInDialog>
    );
  }

  const galleryRef = useRef();
  const [isExportingGLTF, setIsExportingGLTF] = useState(false);
  async function renderInCloudV2() {
    const thumbnail = document
      .querySelector("body canvas")
      .toDataURL("image/png");

    const { camera, size, scene } = renderState;
    setIsExportingGLTF(true);

    const file = new File([await exportGLTF(scene)], "file.glb", {
      type: "model/gltf-binary",
    });
    setIsExportingGLTF(false);

    const resolution = enable4K ? [2048, 2048] : [size.width, size.height];
    const params = {
      backupProject,
      scene: {
        // envMapIntensity: envMapIntensity / 100,
        envMapIntensity: 0,
        camera: {
          position: camera.position.toArray(),
          quaternion: camera.quaternion.toArray(),
          fov: camera.fov,
          resolution,
        },
      },
    };

    await galleryRef.current?.uploadV2(thumbnail, file, params);
  }

  return (
    <>
      <Leva hidden={!leva} />
      {!isMobile && (
        <Container>
          <ToolHeader>
            <a
              rel="noreferrer"
              target="_blank"
              href="https://instashot.xyz/packshot"
            >
              Packshot Maker
            </a>
          </ToolHeader>
          <Body>
            <Gallery
              galleryRef={galleryRef}
              refreshQuota={refreshQuota}
              modelUrl={url}
            />
            <Pane>
              <Viewer style={{ pointerEvents: loading ? "none" : undefined }}>
                {false && !loading && (
                  <Overlay style={{ color: "white", lineHeight: "24px" }}>
                    <div style={{ margin: "120px" }}>
                      <p style={{ color: "#697286" }}>
                        The model you are trying to view is not rendered
                        properly. It may be because your browser isn&apos;t
                        compatible with our latest WebGL technology.
                      </p>
                      <p>
                        We recommend using
                        <span
                          style={{
                            textDecoration: "underline",
                            fontWeight: 700,
                          }}
                        >
                          latest version of Google Chrome
                        </span>
                        to get the best experience.
                      </p>
                    </div>
                  </Overlay>
                )}
                <span
                  style={{
                    visibility: loading ? "hidden" : undefined,
                    pointerEvents: isExportingGLTF ? "none" : "auto",
                  }}
                >
                  {cachedLGL}
                </span>
                {loading && (
                  <Overlay>
                    <Watch width="48" height="48" color="#fff" visible={true} />
                  </Overlay>
                )}
                {!loading && !isExportingGLTF && (
                  <ViewerOverlay>
                    <Button onClick={renderInCloudV2}>
                      <ButtonText style={{ width: 67, height: 28 }}>
                        <AiOutlineCamera size={"1.2em"} />
                      </ButtonText>
                    </Button>
                  </ViewerOverlay>
                )}
              </Viewer>
              <Menu>
                <MenuPane>
                  <MenuPaneHeader>
                    <h1>Lighting</h1>
                  </MenuPaneHeader>
                  <MenuPaneContent>
                    <CardContainer>
                      {LIGHTS.map(({ name, value }) => (
                        <LabelCard
                          key={name}
                          label={name}
                          selected={eqSet(value, lights)}
                          imageSrc={`/presets/${name}.png`}
                          onSelect={() => setLights(value)}
                        />
                      ))}
                    </CardContainer>
                    {false && (
                      <>
                        <span style={{ height: "1em" }} />
                        <SliderOption
                          defaultValue={[envMapIntensity]}
                          label="Intensity"
                          onValueChange={(x) => setEnvMapIntensity(x[0])}
                        />
                      </>
                    )}
                  </MenuPaneContent>
                </MenuPane>
                <Divider />
                <MenuPane>
                  <MenuPaneHeader>
                    <h1>Backdrop</h1>
                  </MenuPaneHeader>
                  <MenuPaneContent>
                    <ColorPicker
                      color={color}
                      onChange={(x) => {
                        setColor(x.hex);
                      }}
                    />
                    <span style={{ height: "1em" }} />
                    <SwitchOption
                      checked={transparentBackdrop}
                      onCheckedChange={setTransparentBackdrop}
                      label="Transparent"
                    />
                    <SliderOption
                      defaultValue={[roughness]}
                      label="Roughness"
                      onValueChange={(x) => setRoughness(x[0])}
                    />
                    <SliderOption
                      defaultValue={[metalness]}
                      label="Metalness"
                      onValueChange={(x) => setMetalness(x[0])}
                    />
                  </MenuPaneContent>
                </MenuPane>
                <Divider />
                <MenuPane>
                  <MenuPaneHeader>
                    <h1>Advanced</h1>
                    {false && (
                      <Badge
                        onClick={() =>
                          (window.location.href =
                            "https://instashot.xyz/?utm_source=packshot")
                        }
                      >
                        <span>Premium</span>
                      </Badge>
                    )}
                  </MenuPaneHeader>
                  <MenuPaneContent>
                    {switch4K}
                    {isAuth && (
                      <span
                        style={{
                          alignSelf: "flex-start",
                          fontSize: 11,
                          color: total - used > 0 ? "#9CA3AF" : "#FFEB00",
                        }}
                      >
                        <b>{total - used}</b> of {total} quota remains today
                      </span>
                    )}
                  </MenuPaneContent>
                </MenuPane>
                <div style={{ marginTop: "auto" }}>
                  <UploadButton onUploadDone={redirectUrl} />
                  <ModelService>
                    Need someone build model for you? Check out
                    <span> </span>
                    <a
                      target="_blank"
                      href="https://instashot.xyz/3d-modeling"
                      rel="noreferrer"
                    >
                      here
                    </a>
                  </ModelService>
                </div>
              </Menu>
            </Pane>
          </Body>
          {isAuth && <Profile />}
        </Container>
      )}
      {isMobile && <MobileOnly />}
    </>
  );
}

const Container = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "#131620",
  minHeight: "100vh",
});

const Body = styled("div", {
  display: "flex",
  gap: 23,
  "@lg": {
    marginBottom: 36,
  },
});

const Pane = styled("div", {
  background: "#171C27",
  borderRadius: "8px",
  display: "flex",
  height: 800,
});

const Menu = styled("div", {
  width: 271,
  height: "100%",
  background: "#1F2937",
  display: "flex",
  flexDirection: "column",
  borderRadius: "0px 3px 3px 0px",
  color: "white",
});

const MenuPaneContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});
const MenuPaneHeader = styled("div", {
  display: "flex",
  h1: {
    fontSize: "$2",
    fontWeight: 600,
  },
  alignItems: "center",
});

const MenuPane = styled("div", {
  paddingTop: "6px",
  width: "100%",
  [`& ${MenuPaneHeader}`]: {
    marginLeft: 17,
  },
  [`& ${MenuPaneContent}`]: {
    marginLeft: 17,
    paddingBottom: "1em",
  },
});

const Divider = styled("span", {
  backgroundColor: "#374151",
  width: "100%",
  height: 1,
});

const Viewer = styled("div", {
  width: 800,
  height: 800,
  position: "relative",
});

const ViewerOverlay = styled("div", {
  position: "absolute",
  width: "100%",
  bottom: 46,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  button: {
    pointerEvents: "auto",
  },
});

const ButtonText = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const StyledLGL = styled(LGL, {
  width: "100%",
  height: "100%",
});

const CardContainer = styled("div", {
  display: "flex",
  gap: 13,
  width: "100%",
  flexWrap: "wrap",
});

const Card = styled("div", {
  width: "100%",
  height: 57,
  background: "#aaa",
  borderRadius: 2,
  cursor: "pointer",
  position: "relative",
  border: "2px solid #131620",
  "&.selected": {
    border: "2px solid #189FFB",
  },
});

const ModelService = styled("div", {
  margin: "1.5em 1em",
  fontSize: 14,
  //background: "#374151",
  color: "#9CA3AF",
  borderRadius: "3px",
  padding: "1em 1em",
  a: {
    color: "#189FFB",
  },
});

function LabelCard({ label, onSelect, imageSrc, selected }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3.61,
        width: 72,
      }}
    >
      <Card onClick={onSelect} className={selected ? "selected" : ""}>
        {imageSrc && (
          <Image
            alt={label}
            layout="fill"
            objectPosition="50% 65%"
            objectFit="cover"
            src={imageSrc}
          />
        )}
      </Card>
      <span
        style={{
          color: "#6B7280",
          fontSize: 11,
          textAlign: "center",
        }}
      >
        {label}
      </span>
    </div>
  );
}

const Overlay = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const Badge = styled("div", {
  marginLeft: 8,
  background: "#FFEB00",
  color: "#1F2937",
  borderRadius: 4,
  userSelect: "none",
  fontSize: "$1",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 26,
  span: {
    margin: "8px 5px",
  },
  cursor: "pointer",
});

function useAuth() {
  const { status } = useSession();
  return status === "authenticated";
}

const eqSet = (x, y) => {
  const xs = new Set([...x]);
  const ys = new Set([...y]);
  return xs.size === ys.size && [...xs].every((x) => ys.has(x));
};

const LIGHTS = [
  {
    name: "C1",
    value: ["gt", "l", "r"],
  },
  {
    name: "C2",
    value: ["gt", "l", "fl", "bl"],
  },
  {
    name: "N1",
    value: ["t", "r"],
  },
  {
    name: "N2",
    value: ["b", "bl", "br"],
  },
  {
    name: "N3",
    value: ["l", "tl", "fl"],
  },
  {
    name: "N4",
    value: ["l", "tb", "tf"],
  },
];

import { getSelectorsByUserAgent } from "react-device-detect";

Packshot.getInitialProps = async (ctx) => {
  const userAgent = ctx.req.headers["user-agent"];
  const { isMobile } = getSelectorsByUserAgent(userAgent);
  return {
    isMobile,
  };
};
